/**
 * RichContent Component
 * 
 * Renders rich text content with support for both:
 * - HTML content (from WYSIWYG editor) - sanitized and rendered safely
 * - Legacy plain text content (with \n and • syntax) - parsed with existing logic
 * 
 * Provides consistent rendering between Admin and MFE.
 */

import React from 'react';
import DOMPurify from 'dompurify';
import './RichContent.scss';

interface RichContentProps {
  content: string | null | undefined;
  className?: string;
}

// Allowed HTML tags for sanitization
const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li', 'a'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'start'];

/**
 * Detect if content is HTML or legacy plain text
 */
function isHtmlContent(content: string): boolean {
  const trimmed = content.trim();
  // Check for HTML tags anywhere in the string (more robust than startsWith for joined content)
  return /<[a-z][\s\S]*>/i.test(trimmed);
}

/**
 * Normalize broken list structure from WYSIWYG editors.
 * 
 * WYSIWYG editors sometimes create:
 *   <ol><li>Label</li></ol><p>Description</p><ol start="2"><li>Label</li></ol><p>Description</p>
 * 
 * This consolidates into proper nested structure:
 *   <ol><li>Label<p>Description</p></li><li>Label<p>Description</p></li></ol>
 */
function normalizeListStructure(html: string): string {
  // Remove empty paragraphs that WYSIWYG editors sometimes add
  const cleaned = html.replace(/<p>\s*<\/p>/gi, '').trim();
  
  // Check if content has the broken pattern: starts with <ol> and has multiple <ol> tags
  const olCount = (cleaned.match(/<ol/gi) || []).length;
  if (olCount < 2 || !cleaned.match(/^<ol/i)) {
    return html;
  }
  
  // Extract each <ol>...<p>... pair
  const pairPattern = /<ol[^>]*>\s*<li>([\s\S]*?)<\/li>\s*<\/ol>\s*<p>([\s\S]*?)<\/p>/gi;
  const items: Array<{ label: string; description: string }> = [];
  
  let match;
  while ((match = pairPattern.exec(cleaned)) !== null) {
    if (match[1] && match[2]) {
      items.push({
        label: match[1].trim(),
        description: match[2].trim()
      });
    }
  }
  
  if (items.length === 0) {
    return html;
  }
  
  // Build consolidated list with proper nesting
  const listItems = items.map(item => {
    const description = item.description ? `<p class="list-item-description">${item.description}</p>` : '';
    return `<li>${item.label}${description}</li>`;
  }).join('');
  
  return `<ol>${listItems}</ol>`;
}

/**
 * Sanitize HTML content
 */
function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}

/**
 * Legacy plain text parser (handles \n and bullets)
 */
function parseLegacyText(text: string): React.ReactNode {
  return text.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line.trim().startsWith('•') ? (
        <span className="bullet-line">{line}</span>
      ) : (
        line
      )}
      {i < text.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));
}

export const RichContent: React.FC<RichContentProps> = ({ content, className }) => {
  if (!content) {
    return null;
  }

  const combinedClassName = className ? `rich-content ${className}` : 'rich-content';

  if (isHtmlContent(content)) {
    // Normalize broken list structures, then sanitize
    const normalizedHtml = normalizeListStructure(content);
    const sanitizedHtml = sanitizeHtml(normalizedHtml);
    return (
      <div 
        className={combinedClassName}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  }

  return (
    <div className={combinedClassName}>
      {parseLegacyText(content)}
    </div>
  );
};

export default RichContent;
